import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import './style.css'
import '../../styles/page-titles.css'
import styled, { keyframes } from 'styled-components';

import { getStoredUser } from '../../utils/safeStorage';
import { formatPriceWithSymbol, getCurrencySymbol } from '../../utils/currency';

import { SidebarContext } from "../../AppRoutes";
import { getPlansAsaas } from "../../services/api";

import PermissionGate from "../../services/PermissionGate";

import { useNavigate, useParams } from "react-router-dom";

import { useFormik } from "formik";
import * as Yup from "yup";

import * as AiIcons from 'react-icons/ai'
import * as SlIcons from 'react-icons/sl'
import * as HiIcons from 'react-icons/hi'

import { toast } from "react-toastify";

// Keyframe animations
const slideUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const bounceIn = keyframes`
    0% {
        opacity: 0;
        transform: scale(0.3);
    }
    50% {
        transform: scale(1.05);
    }
    70% {
        transform: scale(0.9);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

// Main page wrapper
const PageWrapper = styled.div`
    display: flex;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background: #ffffff;
    overflow: initial;
    z-index: 9;
    position: relative;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

// Form container with card effect
const FormContainer = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    animation: ${slideUp} 0.5s ease-out;
    margin-top: 20px;
`;

// Page header
const PageHeader = styled.div`
    animation: ${slideUp} 0.5s ease-out;
    margin-bottom: 24px;

    h1 {
        font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
        font-size: 28px;
        font-weight: 700;
        color: #1e293b;
        margin: 0;
        letter-spacing: -0.5px;
        position: relative;
        display: inline-block;

        &::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 0;
            width: 40px;
            height: 3px;
            background: linear-gradient(90deg, #318cd5, #4fa3e8);
            border-radius: 2px;
        }
    }

    .subtitle {
        margin-top: 16px;
        font-size: 14px;
        color: #64748b;

        .plan-name {
            color: #318cd5;
            font-weight: 600;
        }

        .plan-identifier {
            font-family: 'SF Mono', 'Consolas', monospace;
            background: rgba(49, 140, 213, 0.1);
            padding: 2px 8px;
            border-radius: 4px;
            color: #2574b8;
        }
    }
`;

// Button group container
const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    animation: ${fadeIn} 0.5s ease-out 0.2s backwards;
`;

// Primary button (Update)
const PrimaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #318cd5, #2574b8);
    color: white;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(180deg, rgba(255,255,255,0.15) 0%, transparent 50%);
        pointer-events: none;
    }

    &:hover {
        background: linear-gradient(135deg, #4fa3e8, #318cd5);
        transform: translateY(-2px);
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1), 0 0 20px rgba(49, 140, 213, 0.3);
    }

    &:active {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
        transform: none;
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        transform: scale(1.1);
    }

    &.loading svg {
        animation: ${spin} 0.8s linear infinite;
    }
`;

// Secondary button (Cancel)
const SecondaryButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 24px;
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border: 2px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    background: #ffffff;
    color: #ef4444;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover {
        border-color: #ef4444;
        background: rgba(239, 68, 68, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active {
        transform: translateY(0);
    }

    svg {
        transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover svg {
        transform: scale(1.1);
    }
`;

// Form row container
const FormRow = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 20px;
    flex-wrap: wrap;
    animation: ${slideUp} 0.4s ease-out backwards;
    animation-delay: ${props => props.delay || '0s'};
    margin-bottom: 24px;

    &:last-child {
        margin-bottom: 0;
    }

    @media (max-width: 1024px) {
        flex-direction: column;

        & > div {
            width: 100% !important;
        }
    }
`;

// Input box wrapper
const InputBox = styled.div`
    flex: ${props => props.flex || '1'};
    min-width: ${props => props.minWidth || '200px'};
    animation: ${fadeIn} 0.4s ease-out backwards;
    animation-delay: ${props => props.delay || '0s'};

    label {
        display: block;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #64748b;
        margin-bottom: 8px;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        span {
            color: #ef4444;
            font-weight: 700;
        }
    }

    input, select {
        width: 100%;
        padding: 14px 16px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 15px;
        color: #1e293b;
        background: #f8fafc;
        border: 2px solid #e2e8f0;
        border-radius: 10px;
        transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
        outline: none;

        &::placeholder {
            color: #94a3b8;
        }

        &:hover {
            border-color: #cbd5e1;
            background: #ffffff;
        }

        &:focus {
            border-color: #318cd5;
            background: #ffffff;
            box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }
    }

    select {
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 12px center;
        padding-right: 44px;
        cursor: pointer;
    }

    small {
        display: block;
        margin-top: 6px;
        font-size: 12px;
        color: #94a3b8;
    }

    &.has-error {
        input, select {
            border-color: #ef4444;
            background: rgba(239, 68, 68, 0.05);

            &:focus {
                box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.15), 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
        }
    }
`;

// Error message
const ErrorMessage = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 8px 12px;
    background: rgba(239, 68, 68, 0.1);
    border-radius: 6px;
    font-size: 13px;
    color: #ef4444;
    font-weight: 500;
    animation: ${bounceIn} 0.4s ease-out;

    &::before {
        content: '!';
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        background: #ef4444;
        color: white;
        border-radius: 50%;
        font-size: 11px;
        font-weight: 700;
        flex-shrink: 0;
    }
`;

// Promotional section
const PromoSection = styled.div`
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border: 2px solid rgba(49, 140, 213, 0.2);
    border-radius: 16px;
    padding: 24px;
    margin-top: 24px;
    position: relative;
    overflow: hidden;
    animation: ${slideUp} 0.5s ease-out 0.3s backwards;

    &::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -20%;
        width: 200px;
        height: 200px;
        background: radial-gradient(circle, rgba(49, 140, 213, 0.1) 0%, transparent 70%);
        pointer-events: none;
    }

    h3 {
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 16px;
        font-weight: 700;
        color: #2574b8;
        margin: 0 0 20px 0;
        display: flex;
        align-items: center;
        gap: 8px;
    }
`;

// Promo summary card
const PromoSummary = styled.div`
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    font-family: 'Segoe UI', system-ui, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1e293b;

    /* Reset para evitar interferências */
    * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
    }

    strong {
        color: #2574b8;
        font-weight: 600;
        display: inline;
    }

    .highlight {
        color: #22c55e;
        font-weight: 700;
        font-family: 'SF Mono', 'Consolas', monospace;
        background: rgba(34, 197, 94, 0.1);
        padding: 2px 6px;
        border-radius: 4px;
    }

    .summary-title {
        font-size: 13px;
        font-weight: 700;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 2px solid #e2e8f0;
    }

    .summary-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px dashed #e2e8f0;

        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }

        .label {
            color: #64748b;
            font-size: 13px;
        }

        .value {
            font-weight: 600;
            color: #1e293b;
        }
    }
`;

// Warning section
const WarningSection = styled.div`
    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
    border: 2px solid rgba(245, 158, 11, 0.3);
    border-radius: 16px;
    padding: 20px;
    margin-top: 24px;
    animation: ${slideUp} 0.5s ease-out 0.4s backwards;

    .warning-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 12px;
    }

    .checkbox-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;

        input[type="checkbox"] {
            width: 20px;
            height: 20px;
            accent-color: #f59e0b;
            cursor: pointer;
        }

        label {
            font-weight: 700;
            color: #92400e;
            cursor: pointer;
            font-size: 14px;
        }
    }

    p {
        color: #92400e;
        font-size: 14px;
        margin: 0;
        line-height: 1.6;
    }
`;

// Loading state
const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    animation: ${fadeIn} 0.3s ease-out;

    .spinner {
        width: 60px;
        height: 60px;
        border: 3px solid #e2e8f0;
        border-top-color: #318cd5;
        border-radius: 50%;
        animation: ${spin} 0.8s linear infinite;
    }

    p {
        margin-top: 16px;
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 14px;
        color: #64748b;
    }
`;

// Not found state
const NotFoundContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 300px;
    text-align: center;
    animation: ${fadeIn} 0.5s ease-out;

    svg {
        width: 80px;
        height: 80px;
        color: #94a3b8;
        margin-bottom: 16px;
    }

    h2 {
        font-family: 'Segoe UI', system-ui, sans-serif;
        font-size: 20px;
        font-weight: 600;
        color: #64748b;
        margin: 0 0 16px 0;
    }
`;

const EditPlano = () => {
    const { t } = useTranslation('plansAdmin');
    const userParse = getStoredUser();
    const userID = userParse?._id;

    const empresa = localStorage.getItem('empresa')
    const empresaParse = JSON.parse(empresa)
    const { sidebar } = useContext(SidebarContext);

    const { id } = useParams();
    const navigate = useNavigate();

    const [selectedOption, setSelectedOption] = useState("");
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [plano, setPlano] = useState(null);
    const [updateAssinaturas, setUpdateAssinaturas] = useState(false);

    // Options with i18n translations
    const options = [
        { value: "", label: t('edit.form.selectRecurrence') },
        { value: "WEEKLY", label: t('edit.cycles.weekly') },
        { value: "MONTHLY", label: t('edit.cycles.monthly') },
        { value: "YEARLY", label: t('edit.cycles.yearly') },
    ];

    // Validacoes
    const SignUpSchema = Yup.object().shape({
        title: Yup.string().required(t('edit.form.required')),
        plan_identifier: Yup.string().required(t('edit.form.required')),
        plan_cycle: selectedOption === "" && Yup.string().required(t('edit.form.required')),
        valor_plano: Yup.number().required(t('edit.form.required')).min(0.01, t('edit.form.valueMustBeGreater')),
        promotional_price: Yup.number().min(0, t('edit.form.negativeNotAllowed')),
        promotional_months: Yup.number().min(1, t('edit.form.minMonths')).max(12, t('edit.form.maxMonths')),
        order: Yup.string().required(t('edit.form.required')),
    });

    const formik = useFormik({
        validationSchema: SignUpSchema,
        validateOnBlur: false,
        validateOnChange: false,
        enableReinitialize: true,
        initialValues: {
            title: "",
            plan_identifier: "",
            maxPayments: "",
            valor_plano: 0,
            promotional_price: 19.90,
            promotional_months: 6,
            order: "",
        },
        onSubmit: (values) => {
            const title = values.title;
            const plan_identifier = values.plan_identifier;
            const order = values.order;
            const plan_cycle = selectedOption;
            const maxPayments = values.maxPayments;
            const valor_plano = Number(values.valor_plano);
            const promotional_price = Number(values.promotional_price);
            const promotional_months = Number(values.promotional_months);

            handleSubmitAPI(title, plan_identifier, order, plan_cycle, maxPayments, valor_plano, promotional_price, promotional_months);
        },
    });

    // Carregar dados do plano existente
    useEffect(() => {
        const loadPlano = async () => {
            try {
                setLoading(true);
                const response = await getPlansAsaas();
                const planos = response.data?.planos?.planos || [];
                const planoEncontrado = planos.find(p => p._id === id);

                if (planoEncontrado) {
                    setPlano(planoEncontrado);
                    setSelectedOption(planoEncontrado.plan_cycle || "");

                    formik.setValues({
                        title: planoEncontrado.name || "",
                        plan_identifier: planoEncontrado.plan_identifier || "",
                        maxPayments: planoEncontrado.max_cycles || "",
                        valor_plano: planoEncontrado.valor_plano || 0,
                        promotional_price: planoEncontrado.promotional_price || 19.90,
                        promotional_months: planoEncontrado.promotional_months || 6,
                        order: planoEncontrado.order || "",
                    });
                } else {
                    toast(t('edit.toasts.notFound'), { autoClose: 5000, type: "error" });
                    navigate("/list-plans-admin");
                }
            } catch (error) {
                process.env.REACT_APP_ISDEVELOP === 'true' && console.error("Erro ao carregar plano:", error);
                toast(t('edit.toasts.loadError'), { autoClose: 5000, type: "error" });
                navigate("/list-plans-admin");
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadPlano();
        }
    }, [id]);

    const handleBack = () => {
        navigate("/list-plans-admin");
    }

    const handleSubmitAPI = async (title, plan_identifier, order, plan_cycle, maxPayments, valor_plano, promotional_price, promotional_months) => {
        setIsSubmitting(true);
        try {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.log("Atualizando plano!", {
                id, title, plan_identifier, order, plan_cycle, maxPayments,
                valor_plano, promotional_price, promotional_months, updateAssinaturas
            });

            // TODO: Implementar API de atualizacao de plano
            // const response = await updatePlanoAdmin(id, dados...);

            toast(t('edit.toasts.updateSuccess'), { autoClose: 5000, type: "success" });

            if (updateAssinaturas) {
                toast(t('edit.toasts.subscriptionsUpdate'), { autoClose: 8000, type: "info" });
            }

            navigate("/list-plans-admin");

        } catch (error) {
            process.env.REACT_APP_ISDEVELOP === 'true' && console.error('Erro ao atualizar plano:', error);
            toast(t('edit.toasts.updateError'), { autoClose: 5000, type: "error" });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleCurrencyChange = (e, formik, fieldName) => {
        const inputValue = e.target.value;
        const rawValue = inputValue.replace(/\D/g, "");

        if (rawValue.length === 0) {
            formik.setFieldValue(fieldName, 0);
            e.target.value = formatPriceWithSymbol(0);
            return;
        }

        const numericValue = Number(rawValue) / 100;
        e.target.value = formatPriceWithSymbol(numericValue);
        formik.setFieldValue(fieldName, Number(rawValue) / 100);
    };

    const handleOptionChange = (event) => {
        setSelectedOption(event.target.value);
    };

    if (loading) {
        return (
            <PermissionGate permissions={['AdminOnly']}>
                <PageWrapper sidebar={sidebar}>
                    <div className="w-100 p-4 p-md-5">
                        <LoadingContainer>
                            <div className="spinner" />
                            <p>{t('edit.loading')}</p>
                        </LoadingContainer>
                    </div>
                </PageWrapper>
            </PermissionGate>
        );
    }

    if (!plano) {
        return (
            <PermissionGate permissions={['AdminOnly']}>
                <PageWrapper sidebar={sidebar}>
                    <div className="w-100 p-4 p-md-5">
                        <NotFoundContainer>
                            <AiIcons.AiOutlineFileSearch />
                            <h2>{t('edit.notFound')}</h2>
                            <SecondaryButton onClick={handleBack}>
                                <SlIcons.SlActionUndo style={{ fontSize: "16px" }} />
                                {t('edit.buttons.back')}
                            </SecondaryButton>
                        </NotFoundContainer>
                    </div>
                </PageWrapper>
            </PermissionGate>
        );
    }

    return (
        <PermissionGate permissions={['AdminOnly']}>
            <PageWrapper sidebar={sidebar}>
                <div className="w-100 p-4 p-md-5">
                    <form onSubmit={formik.handleSubmit}>
                        <PageHeader>
                            <h1>{t('edit.title')}</h1>
                            <p className="subtitle">
                                {t('edit.editing')} <span className="plan-name">{plano.name}</span>{' '}
                                <span className="plan-identifier">{plano.plan_identifier}</span>
                            </p>
                        </PageHeader>

                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "24px" }}>
                            <ButtonGroup>
                                <PrimaryButton type="submit" disabled={isSubmitting} className={isSubmitting ? 'loading' : ''}>
                                    {isSubmitting ? (
                                        <AiIcons.AiOutlineLoading3Quarters style={{ fontSize: "20px" }} />
                                    ) : (
                                        <AiIcons.AiOutlineSave style={{ fontSize: "20px" }} />
                                    )}
                                    {t('edit.buttons.update')}
                                </PrimaryButton>

                                <SecondaryButton type="button" onClick={handleBack}>
                                    <SlIcons.SlActionUndo style={{ fontSize: "16px" }} />
                                    {t('edit.buttons.cancel')}
                                </SecondaryButton>
                            </ButtonGroup>
                        </div>

                        <FormContainer>
                            <FormRow delay="0.1s">
                                <InputBox
                                    flex="1.5"
                                    delay="0.1s"
                                    className={formik.errors.title ? 'has-error' : ''}
                                >
                                    <label>{t('edit.form.planName')}<span>*</span></label>
                                    <input
                                        type="text"
                                        name="title"
                                        placeholder={t('edit.form.planNamePlaceholder')}
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.title && <ErrorMessage>{formik.errors.title}</ErrorMessage>}
                                </InputBox>

                                <InputBox
                                    flex="1.5"
                                    delay="0.15s"
                                    className={formik.errors.plan_identifier ? 'has-error' : ''}
                                >
                                    <label>{t('edit.form.planIdentifier')}<span>*</span></label>
                                    <input
                                        type="text"
                                        name="plan_identifier"
                                        placeholder={t('edit.form.planIdentifierPlaceholder')}
                                        value={formik.values.plan_identifier}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.plan_identifier && <ErrorMessage>{formik.errors.plan_identifier}</ErrorMessage>}
                                </InputBox>

                                <InputBox
                                    flex="0.8"
                                    minWidth="120px"
                                    delay="0.2s"
                                    className={formik.errors.order ? 'has-error' : ''}
                                >
                                    <label>{t('edit.form.order')}<span>*</span></label>
                                    <input
                                        type="number"
                                        name="order"
                                        placeholder={t('edit.form.orderPlaceholder')}
                                        value={formik.values.order}
                                        onChange={formik.handleChange}
                                    />
                                    {formik.errors.order && <ErrorMessage>{formik.errors.order}</ErrorMessage>}
                                </InputBox>
                            </FormRow>

                            <FormRow delay="0.2s">
                                <InputBox flex="1" delay="0.25s">
                                    <label>{t('edit.form.recurrence')}<span>*</span></label>
                                    <select value={selectedOption} onChange={handleOptionChange}>
                                        {options.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </InputBox>

                                <InputBox
                                    flex="1"
                                    delay="0.3s"
                                    className={formik.errors.valor_plano ? 'has-error' : ''}
                                >
                                    <label>{t('edit.form.planValue')}<span>*</span></label>
                                    <input
                                        type="text"
                                        name="valor_plano"
                                        placeholder={formatPriceWithSymbol(0)}
                                        defaultValue={formatPriceWithSymbol(formik.values.valor_plano)}
                                        onChange={(e) => handleCurrencyChange(e, formik, 'valor_plano')}
                                    />
                                    {formik.errors.valor_plano && <ErrorMessage>{formik.errors.valor_plano}</ErrorMessage>}
                                </InputBox>

                                <InputBox flex="1" delay="0.35s">
                                    <label>{t('edit.form.maxCharges')}</label>
                                    <input
                                        type="number"
                                        name="maxPayments"
                                        placeholder={t('edit.form.maxChargesPlaceholder')}
                                        value={formik.values.maxPayments}
                                        onChange={formik.handleChange}
                                    />
                                </InputBox>
                            </FormRow>

                            {/* Promotional Section */}
                            <PromoSection>
                                <h3>
                                    <HiIcons.HiOutlineSparkles style={{ fontSize: "20px" }} />
                                    {t('edit.promotional.title')}
                                </h3>

                                <FormRow delay="0.4s" style={{ marginBottom: 0 }}>
                                    <InputBox flex="1" delay="0.45s">
                                        <label>{t('edit.promotional.price')}</label>
                                        <input
                                            type="text"
                                            name="promotional_price"
                                            placeholder={t('edit.promotional.pricePlaceholder')}
                                            defaultValue={formatPriceWithSymbol(formik.values.promotional_price)}
                                            onChange={(e) => handleCurrencyChange(e, formik, 'promotional_price')}
                                        />
                                        <small>{t('edit.promotional.priceHint')}</small>
                                    </InputBox>

                                    <InputBox flex="1" delay="0.5s">
                                        <label>{t('edit.promotional.duration')}</label>
                                        <input
                                            type="number"
                                            name="promotional_months"
                                            min="1"
                                            max="12"
                                            placeholder={t('edit.promotional.durationPlaceholder')}
                                            value={formik.values.promotional_months}
                                            onChange={formik.handleChange}
                                        />
                                        <small>{t('edit.promotional.durationHint')}</small>
                                    </InputBox>

                                    <div style={{ flex: '1.2', minWidth: '250px' }}>
                                        <PromoSummary>
                                            <div className="summary-title">
                                                {t('edit.promotional.summary')}
                                            </div>
                                            <div className="summary-row">
                                                <span className="label">{t('edit.promotional.firstMonths', { count: formik.values.promotional_months })}</span>
                                                <span className="highlight">{formatPriceWithSymbol(formik.values.promotional_price)}</span>
                                            </div>
                                            <div className="summary-row">
                                                <span className="label">{t('edit.promotional.after')}</span>
                                                <span className="value">{formatPriceWithSymbol(formik.values.valor_plano)}{t('edit.promotional.perMonth')}</span>
                                            </div>
                                            <div className="summary-row">
                                                <span className="label"><strong>{t('edit.promotional.discount')}</strong></span>
                                                <span className="highlight">{formatPriceWithSymbol(formik.values.valor_plano - formik.values.promotional_price)}</span>
                                            </div>
                                        </PromoSummary>
                                    </div>
                                </FormRow>
                            </PromoSection>

                            {/* Warning Section */}
                            <WarningSection>
                                <div className="warning-header">
                                    <HiIcons.HiOutlineExclamation style={{ fontSize: "24px", color: "#f59e0b" }} />
                                    <div className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            id="updateAssinaturas"
                                            checked={updateAssinaturas}
                                            onChange={(e) => setUpdateAssinaturas(e.target.checked)}
                                        />
                                        <label htmlFor="updateAssinaturas">
                                            {t('edit.subscriptions.updateLabel')}
                                        </label>
                                    </div>
                                </div>
                                <p>
                                    <strong>{t('edit.subscriptions.warning')}</strong> {t('edit.subscriptions.warningMessage')}
                                </p>
                            </WarningSection>
                        </FormContainer>
                    </form>
                </div>
            </PageWrapper>
        </PermissionGate>
    );
};

export default EditPlano;
